import React, { useContext, useEffect, useState } from 'react';
import EnvironmentVariables from './EnvironmentVariables';

const ENV_VARIABLES_FILE_PATH = `${process.env.PUBLIC_URL}/env.json`;

const EnvironmentContext = React.createContext<EnvironmentVariables | null>(null);

export const useEnvironment = (): EnvironmentVariables => {
  const environmentVariables = useContext(EnvironmentContext);
  if (!environmentVariables) {
    throw new Error('useEnvironment must be used inside EnvironmentProvider');
  }
  return environmentVariables;
};

export const EnvironmentConsumer = EnvironmentContext.Consumer;

export type EnvironmentProviderProps = {
  children: React.ReactNode;
};

export const EnvironmentProvider = ({ children }: EnvironmentProviderProps): JSX.Element => {
  const [environmentVariables, setEnvironmentVariables] = useState<EnvironmentVariables | null>(null);

  useEffect(() => {
    async function fetchEnvironmentContext(): Promise<EnvironmentVariables> {
      const response = await fetch(ENV_VARIABLES_FILE_PATH);
      const responseJson = (await response.json()) as EnvironmentVariables;
      return responseJson;
    }

    fetchEnvironmentContext()
      .then((responseJson) => {
        setEnvironmentVariables(responseJson);
      })
      .catch(() => {
        throw new Error('Could not fetch environment variables');
      });
  }, []);

  if (environmentVariables) {
    return <EnvironmentContext.Provider value={environmentVariables}>{children}</EnvironmentContext.Provider>;
  }

  return <>Loading…</>;
};
